import { Button, Flex, Heading, NativeSelect, Spinner, Stack, Table, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { regions } from '@planner/config';
import { orderStatuses, type Order } from '@planner/shared';
import { useAuth } from '../hooks/useAuth.js';
import { exportOrdersCsv, listOrders } from '../lib/api.js';
import { formatCurrency } from '../lib/format.js';

export function Orders() {
  const { getToken, hasRole } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [region, setRegion] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const load = useCallback(
    async (append: boolean, fromCursor?: string) => {
      setLoading(true);
      setError(undefined);
      try {
        const token = await getToken();
        const result = await listOrders(token, { region, status, cursor: fromCursor });
        setOrders((current) => (append ? [...current, ...result.orders] : result.orders));
        setCursor(result.nextCursor);
      } catch (cause) {
        setError((cause as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [getToken, region, status],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const download = async () => {
    try {
      const token = await getToken();
      const blob = await exportOrdersCsv(token, { region, status });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setError((cause as Error).message);
    }
  };

  return (
    <Stack gap={5} data-name="orders-page">
      <Flex align="center" gap={3} wrap="wrap">
        <Heading size="lg">Orders</Heading>
        <Flex gap={2} ml="auto" align="center">
          <NativeSelect.Root size="sm" width="140px" data-name="filter-region">
            <NativeSelect.Field value={region} onChange={(e) => setRegion(e.target.value)}>
              <option value="">All regions</option>
              {regions.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <NativeSelect.Root size="sm" width="140px" data-name="filter-status">
            <NativeSelect.Field value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {orderStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          {hasRole('Admin', 'Sales') && (
            <Button size="sm" variant="outline" onClick={download} data-name="export-csv">
              Export CSV
            </Button>
          )}
        </Flex>
      </Flex>

      {error && (
        <Text color="red.500" data-name="orders-error">
          {error}
        </Text>
      )}

      <Table.Root size="sm" data-name="orders-table">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Reference</Table.ColumnHeader>
            <Table.ColumnHeader>Status</Table.ColumnHeader>
            <Table.ColumnHeader>Region</Table.ColumnHeader>
            <Table.ColumnHeader>Customer</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Total</Table.ColumnHeader>
            <Table.ColumnHeader>Updated</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {orders.map((order) => (
            <Table.Row key={order.referenceCode}>
              <Table.Cell>
                <RouterLink to={`/orders/${order.referenceCode}`}>
                  <Text color="brand.600" fontWeight="medium">
                    {order.referenceCode}
                  </Text>
                </RouterLink>
              </Table.Cell>
              <Table.Cell>{order.status}</Table.Cell>
              <Table.Cell>{order.region.toUpperCase()}</Table.Cell>
              <Table.Cell>{order.customer?.email ?? '—'}</Table.Cell>
              <Table.Cell textAlign="right">
                {order.totals ? formatCurrency(order.totals.total, order.totals.currency) : '—'}
              </Table.Cell>
              <Table.Cell>{new Date(order.updatedAt).toLocaleString()}</Table.Cell>
            </Table.Row>
          ))}
          {orders.length === 0 && loading && (
            <Table.Row>
              <Table.Cell colSpan={6}>
                <Flex justify="center" py={6} data-name="orders-loading">
                  <Spinner color="brand.500" />
                </Flex>
              </Table.Cell>
            </Table.Row>
          )}
          {orders.length === 0 && !loading && (
            <Table.Row>
              <Table.Cell colSpan={6}>
                <Text color="fg.muted" py={4} textAlign="center">
                  No orders found.
                </Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>

      {cursor && (
        <Button
          alignSelf="center"
          variant="outline"
          loading={loading}
          onClick={() => void load(true, cursor)}
          data-name="load-more"
        >
          Load more
        </Button>
      )}
    </Stack>
  );
}
